import {
  type Game,
  postScheduleInternalMutation,
  Schedule,
  type ScheduleInternal,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { SCHEDULES_QUERY_KEY } from "./FieldSchedule.queries";

export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    ...postScheduleInternalMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SCHEDULES_QUERY_KEY],
        exact: false,
        refetchType: "all",
      });
    },
  });

  const deleteEvent = (schedule: Schedule) =>
    mutateAsync({
      body: {
        id: schedule.id,
        machine_uuid: schedule.machine_uuid,
        year_month: schedule.year_month,
        schedule_time_s: schedule.schedule_time_s,
        internal: null,
      },
    });

  return { deleteEvent, isDeletingEvent: isPending };
};

function rewriteGameMachineUuid(
  game: Game | null | undefined,
  newMachineUuid: string,
): Game | null | undefined {
  if (!game) return game;
  return {
    ...game,
    machine_uuid: newMachineUuid,
    field_uuid: newMachineUuid,
    times: game.times?.map((part) => ({
      ...part,
      machine_uuid: newMachineUuid,
      field_uuid: newMachineUuid,
    })),
  } as Game;
}

function rewriteInternalMachineUuid(
  internal: ScheduleInternal | null | undefined,
  newMachineUuid: string,
): ScheduleInternal | null | undefined {
  if (!internal) return internal;
  return {
    ...internal,
    game: rewriteGameMachineUuid(internal.game, newMachineUuid),
  };
}

export const useMoveEvent = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    ...postScheduleInternalMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SCHEDULES_QUERY_KEY],
        exact: false,
        refetchType: "all",
      });
    },
  });

  const moveEvent = async (schedule: Schedule, newMachineUuid: string) => {
    await mutateAsync({
      body: {
        id: schedule.id,
        machine_uuid: newMachineUuid,
        year_month: schedule.year_month,
        schedule_time_s: schedule.schedule_time_s,
        internal: rewriteInternalMachineUuid(schedule.internal, newMachineUuid),
      },
    });
  };

  return { moveEvent, isMovingEvent: isPending };
};
